<script lang="ts" setup>
import type { ExchangeGroupingFilterEnumType } from '@forgd/shared/enums'
import type { Component } from 'vue'
import { formatSignedPercent } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Card } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const props = defineProps<{
  // Same start date the parent uses for its listings-by-exchange query — passed
  // explicitly so this child reuses the parent's pinia/colada cache key (no
  // extra fetch) instead of inventing its own scoping.
  listingsStartDate: string
}>()

// "Avg. 7D Return" on the Primary Listing Performance endpoint is the '1w' bucket.
const PRIMARY_RETURN_TIMEFRAME = '1w'
// Follow-on impact is bucketed as day0/day1/day7/day30 — day7 ≈ 7D return.
const FOLLOW_ON_RETURN_TIMEFRAME = 'day7'
// The feeder card is scoped to Binance as the destination exchange (matches Figma).
const FEEDER_EXCHANGE_ID = 'binance'
const FEEDER_EXCHANGE_LABEL = 'Binance'

// Same query+key as the parent; pinia/colada serves it from cache.
const listingsByExchangeQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/listings-by-exchange', {
    params: () => ({ query: { startDate: props.listingsStartDate } }),
  }),
  refetchOnWindowFocus: false,
})

const primaryListingPerformanceQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/primary-listing-performance', {
    params: () => ({ query: { exchangeGroup: 'individual' as ExchangeGroupingFilterEnumType } }),
  }),
  refetchOnWindowFocus: false,
})

const followOnListingImpactQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/follow-on-listing-impact', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const preExistingPresenceQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/pre-existing-exchange-presence', {
    params: () => ({ query: { exchangeId: FEEDER_EXCHANGE_ID } }),
  }),
  refetchOnWindowFocus: false,
})

const exchangeLogoMap = computed(() => {
  const map = new Map<string, string | null>()
  for (const item of listingsByExchangeQuery.data.value?.byExchange ?? []) {
    map.set(item.exchangeName.toLowerCase(), item.logo)
  }
  return map
})

function resolveExchangeLogo(name: string | undefined): string | null {
  if (!name) {
    return null
  }
  return exchangeLogoMap.value.get(name.toLowerCase()) ?? null
}

const bestPrimaryExchange = computed(() => {
  const points = primaryListingPerformanceQuery.data.value?.avgPriceChangeByExchange ?? []
  const at = points.filter(p => p.timeframe === PRIMARY_RETURN_TIMEFRAME)
  if (!at.length) {
    return null
  }
  const best = at.reduce((a, b) => (b.value > a.value ? b : a))
  // Match the per-exchange pop/volume stats by lowercased name (mirrors resolveExchangeLogo).
  const stats = primaryListingPerformanceQuery.data.value?.exchangeStats
    ?.find(s => s.exchangeName.toLowerCase() === best.exchangeName.toLowerCase())
  return {
    name: best.exchangeName,
    logo: resolveExchangeLogo(best.exchangeName),
    return7d: best.value,
    initialPopPct: stats?.avgInitialPopPercentage ?? null,
    day1VolumeUsd: stats?.avgDay1VolumeUsd ?? null,
  }
})

const bestFollowOnExchange = computed(() => {
  const items = followOnListingImpactQuery.data.value?.exchanges ?? []
  if (!items.length) {
    return null
  }
  const best = items.reduce((a, b) =>
    (b[FOLLOW_ON_RETURN_TIMEFRAME].listingBoostPct > a[FOLLOW_ON_RETURN_TIMEFRAME].listingBoostPct ? b : a),
  )
  return {
    name: best.exchange,
    logo: best.logo ?? resolveExchangeLogo(best.exchange),
    return7d: best[FOLLOW_ON_RETURN_TIMEFRAME].listingBoostPct,
    initialPopPct: best.day0.listingBoostPct,
    day1VolumeUsd: best.day1.volumeUsd,
  }
})

const topFeederExchange = computed(() => {
  const items = preExistingPresenceQuery.data.value?.perExchange ?? []
  if (!items.length) {
    return null
  }
  const best = items.reduce((a, b) => (b.percentage > a.percentage ? b : a))
  return {
    name: best.exchangeName,
    logo: best.logoUrl ?? resolveExchangeLogo(best.exchangeName),
    percentage: best.percentage,
  }
})

// Each stat label is a list of inline segments — strings render as text,
// components render as icons. Lets any tile mix text + icons uniformly.
interface TileStat { value: string, label: Array<string | Component> }

interface Tile {
  title: string
  name: string
  logo: string | null
  // null = this tile has no 7D return slot (e.g., the feeder tile).
  return7d: number | null
  stats: TileStat[]
}

// A tile slot is `Tile` when its source data is ready, `null` while loading.
// The template renders the full tile or a skeleton placeholder based on this.
const tiles = computed<Array<Tile | null>>(() => [
  bestPrimaryExchange.value
    ? {
        title: 'Best Primary Exchange',
        name: bestPrimaryExchange.value.name,
        logo: bestPrimaryExchange.value.logo,
        return7d: bestPrimaryExchange.value.return7d,
        stats: [
          { value: formatValue(bestPrimaryExchange.value.initialPopPct, 'percent'), label: ['Avg. Initial Pop'] },
          { value: formatValue(bestPrimaryExchange.value.day1VolumeUsd, 'currency:short,0'), label: ['Avg. 1D Vol'] },
        ],
      }
    : null,
  bestFollowOnExchange.value
    ? {
        title: 'Best Follow-On Exchange',
        name: bestFollowOnExchange.value.name,
        logo: bestFollowOnExchange.value.logo,
        return7d: bestFollowOnExchange.value.return7d,
        stats: [
          { value: formatValue(bestFollowOnExchange.value.initialPopPct, 'percent'), label: ['Avg. Initial Pop'] },
          { value: formatValue(bestFollowOnExchange.value.day1VolumeUsd, 'currency:short,0'), label: ['Avg. 1D Vol'] },
        ],
      }
    : null,
  topFeederExchange.value
    ? {
        title: `Top Feeder Exchange for ${FEEDER_EXCHANGE_LABEL} Listings`,
        name: topFeederExchange.value.name,
        logo: topFeederExchange.value.logo,
        return7d: null,
        stats: [
          { value: formatValue(topFeederExchange.value.percentage, 'percent'), label: ['listings', ArrowRightIcon, `${FEEDER_EXCHANGE_LABEL} listings`] },
        ],
      }
    : null,
])
</script>

<template>
  <Card class="grid grid-cols-1 divide-y divide-default lg:grid-cols-3 lg:divide-x lg:divide-y-0">
    <div v-for="(tile, ti) in tiles" :key="ti" class="flex flex-col p-4">
      <template v-if="tile">
        <span class="text-xs font-semibold text-primal">{{ tile.title }}</span>
        <div class="mt-4 flex items-center justify-between gap-2">
          <div class="flex min-w-0 items-center gap-2">
            <Avatar class="size-4 shrink-0 border border-default bg-white">
              <AvatarImage v-if="tile.logo" :src="tile.logo" :alt="tile.name" />
              <AvatarFallback class="text-2xs">
                {{ tile.name.slice(0, 1) }}
              </AvatarFallback>
            </Avatar>
            <span class="truncate text-sm font-semibold text-primal capitalize">{{ tile.name }}</span>
          </div>
          <div v-if="tile.return7d != null" class="shrink-0 text-xs leading-none text-secondary">
            Avg. 7D Return:
            <span
              class="font-semibold"
              :class="tile.return7d >= 0 ? 'text-success' : 'text-error'"
            >
              {{ formatSignedPercent(tile.return7d, 1) }}
            </span>
          </div>
        </div>
        <hr class="my-2 border-default">
        <div class="flex items-center justify-between gap-4 text-xs">
          <span v-for="(stat, si) in tile.stats" :key="si" class="inline-flex items-center gap-1 font-mono font-medium">
            <span class="text-primal">{{ stat.value }}</span>
            <span class="inline-flex items-center gap-1 text-secondary">
              <template v-for="(seg, sj) in stat.label" :key="sj">
                <component :is="seg" v-if="typeof seg !== 'string'" class="size-3" aria-hidden="true" />
                <span v-else>{{ seg }}</span>
              </template>
            </span>
          </span>
        </div>
      </template>
      <template v-else>
        <Skeleton class="h-4 w-32" />
        <div class="mt-4 flex items-center justify-between gap-2">
          <div class="flex min-w-0 items-center gap-2">
            <Skeleton class="size-4 shrink-0 rounded-full" />
            <Skeleton class="h-4 w-24" />
          </div>
          <Skeleton class="h-3 w-32" />
        </div>
        <hr class="my-2 border-default">
        <div class="flex items-center justify-between gap-4">
          <Skeleton class="h-3 w-32" />
          <Skeleton class="h-3 w-32" />
        </div>
      </template>
    </div>
  </Card>
</template>
